import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from '../types';
import {
  bootstrapRootUser,
  changeOwnPassword,
  getAuthStatus,
  getCurrentUser,
  login as loginApi,
  logout as logoutApi,
  setAuthToken,
} from '../services/api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  setupRequired: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  bootstrap: (name: string, email: string, password: string, setupKey?: string) => Promise<{ success: boolean; error?: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const inactivityTimerRef = useRef<number | null>(null);
  const lastActivityAtRef = useRef<number>(Date.now());

  const performLogout = useCallback(async () => {
    try {
      await logoutApi();
    } finally {
      setAuthToken(null);
      setUser(null);
    }
  }, []);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    lastActivityAtRef.current = Date.now();
    clearInactivityTimer();
    inactivityTimerRef.current = window.setTimeout(() => {
      void performLogout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearInactivityTimer, performLogout]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const status = await getAuthStatus();
        if (status.success && status.setupRequired) {
          setSetupRequired(true);
          setAuthToken(null);
          setUser(null);
          return;
        }

        setSetupRequired(false);
        const response = await getCurrentUser();
        if (response.success && response.user) {
          setUser(response.user);
        } else {
          setAuthToken(null);
          setUser(null);
        }
      } catch {
        setAuthToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (!user) {
      clearInactivityTimer();
      return;
    }

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    const handleAppResume = () => {
      if (document.visibilityState === 'hidden') return;
      const inactiveFor = Date.now() - lastActivityAtRef.current;
      if (inactiveFor >= INACTIVITY_TIMEOUT_MS) {
        void performLogout();
        return;
      }
      resetInactivityTimer();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleAppResume);
    window.addEventListener('focus', handleAppResume);
    window.addEventListener('pageshow', handleAppResume);

    resetInactivityTimer();

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleAppResume);
      window.removeEventListener('focus', handleAppResume);
      window.removeEventListener('pageshow', handleAppResume);
      clearInactivityTimer();
    };
  }, [user, resetInactivityTimer, clearInactivityTimer, performLogout]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    login: async (email: string, password: string) => {
      try {
        const response = await loginApi(email, password);
        if (response.success && response.user) {
          setSetupRequired(false);
          setUser(response.user);
          return { success: true };
        }
        return { success: false, error: response.error || 'Login failed' };
      } catch (error) {
        return { success: false, error: String(error) };
      }
    },
    bootstrap: async (name: string, email: string, password: string, setupKey?: string) => {
      try {
        const response = await bootstrapRootUser(name, email, password, setupKey);
        if (response.success && response.user) {
          setSetupRequired(false);
          setUser(response.user);
          return { success: true };
        }
        return { success: false, error: response.error || 'Bootstrap failed' };
      } catch (error) {
        return { success: false, error: String(error) };
      }
    },
    changePassword: async (currentPassword: string, newPassword: string) => {
      try {
        const response = await changeOwnPassword(currentPassword, newPassword);
        if (response.success && response.user) {
          setUser(response.user);
          return { success: true };
        }
        return { success: false, error: response.error || 'Password change failed' };
      } catch (error) {
        return { success: false, error: String(error) };
      }
    },
    logout: performLogout,
    setupRequired,
  }), [user, loading, performLogout, setupRequired]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
